import React, { useMemo, useState } from 'react'
import {
    BadgeIndianRupee,
    CalendarDays,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    FileText,
    MessageSquareQuote,
    Sparkles,
    Star,
    Ticket,
    UserRound
} from 'lucide-react'
import { humanReadableTimePassed } from '../../Helper/calculations'
import styles from '../Feedback/EachFeedbackBox.module.css'

const clampStar = (value) => {
    const parsed = parseInt(value, 10)
    if (!Number.isFinite(parsed)) return 0
    return Math.max(1, Math.min(5, parsed))
}

const ratingTone = (rating) => {
    if (rating >= 5) return styles.ratingExcellent
    if (rating >= 4) return styles.ratingGood
    if (rating >= 3) return styles.ratingOkay
    if (rating >= 2) return styles.ratingLow
    return styles.ratingCritical
}

const toDateOnly = (value) => {
    if (!value) return '—'
    const asString = String(value)
    return asString.length >= 10 ? asString.substring(0, 10) : asString
}

const getInitials = (name = '') =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() || '')
        .join('') || 'U'

const getRatingInsight = (rating) => {
    if (rating >= 5) return { label: 'Delighted', tone: styles.moodExcellent }
    if (rating >= 4) return { label: 'Positive', tone: styles.moodGood }
    if (rating >= 3) return { label: 'Neutral', tone: styles.moodOkay }
    if (rating >= 2) return { label: 'Needs Attention', tone: styles.moodLow }
    return { label: 'Critical', tone: styles.moodCritical }
}

const EachFeedbackBox = ({ item }) => {
    const [showMore, setShowMore] = useState(false)

    const rating = useMemo(() => clampStar(item?.selectedStar), [item?.selectedStar])
    const ratingInsight = useMemo(() => getRatingInsight(rating), [rating])
    const clientInitials = useMemo(() => getInitials(item?.clientName || ''), [item?.clientName])
    const amount = useMemo(() => {
        const parsed = parseFloat(item?.amountToBeCollected || '0')
        return Number.isFinite(parsed) ? Math.round(parsed) : 0
    }, [item?.amountToBeCollected])

    const factors = useMemo(() => {
        const parsed = [
            { label: 'Salon Environment', value: parseInt(item?.starSelectedSalonEnvironment || '0', 10), className: styles.factorEnvironment },
            { label: 'Service Result', value: parseInt(item?.starSelectedServiceResults || '0', 10), className: styles.factorService },
            { label: 'Staff Behaviour', value: parseInt(item?.starSelectedStaffBehaviour || '0', 10), className: styles.factorStaff },
        ]

        return parsed.filter(entry => Number.isFinite(entry.value) && entry.value > 0)
    }, [item?.starSelectedSalonEnvironment, item?.starSelectedServiceResults, item?.starSelectedStaffBehaviour])

    return (
        <div className={styles.card}>
            <div className={styles.topRow}>
                <div className={styles.identityBlock}>
                    <div className={styles.avatar}>{clientInitials}</div>

                    <div className={styles.identityContent}>
                        <div className={styles.nameRow}>
                            <div className={styles.clientRow}>
                                <UserRound size={13} />
                                <span className={styles.clientName}>{item?.clientName || 'Unknown Client'}</span>
                            </div>

                            <span className={`${styles.moodBadge} ${ratingInsight.tone}`}>
                                {ratingInsight.label}
                            </span>
                        </div>

                        <div className={styles.metaRow}>
                            <span className={styles.metaChip}>
                                <CalendarDays size={11} />
                                Visited: {toDateOnly(item?.payStatusLastUpdatedAt)}
                            </span>
                            <span className={styles.metaChip}>
                                <Sparkles size={11} />
                                {humanReadableTimePassed(item?.ratedOn)}
                            </span>
                            <span className={styles.metaChip}>
                                <BadgeIndianRupee size={11} />
                                Ticket: ₹{amount}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={styles.ratingBlock}>
                    <div className={`${styles.ratingPill} ${ratingTone(rating)}`}>
                        <Star size={13} fill="currentColor" />
                        <span>{rating}</span>
                    </div>
                    <span className={styles.ratingCaption}>Overall Rating</span>
                </div>
            </div>

            {/* <div className={styles.summaryStrip}>
                <span className={styles.summaryChip}>
                    <FileText size={11} /> Review ID: {String(item?.reviewIdentifier || 'NA').slice(-10)}
                </span>
                <span className={styles.summaryChip}>
                    <Ticket size={11} /> Booking: {String(item?.bookingIdentifier || 'NA').slice(-10)}
                </span>
            </div> */}

            {!!item?.careToShareALilMoreTxt && (
                <div className={styles.feedbackNote}>
                    <MessageSquareQuote size={14} />
                    <div className={styles.feedbackNoteContent}>
                        <span className={styles.feedbackNoteTitle}>Client Note</span>
                        <span>{item.careToShareALilMoreTxt}</span>
                    </div>
                </div>
            )}

            {factors.length > 0 && (
                <div className={styles.factorsGrid}>
                    {factors.map((factor) => (
                        <div key={factor.label} className={`${styles.factorCard} ${factor.className}`}>
                            <div className={styles.factorLeft}>
                                <span className={styles.factorTitle}>{factor.label}</span>
                                <span className={styles.factorSubtext}>Scored by client</span>
                            </div>
                            <span className={styles.factorValue}>
                                <Star size={10} fill="currentColor" />
                                {factor.value}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.actionsRow}>
                <div className={styles.actionsTopRow}>
                    <button className={styles.toggleDetailsBtn} onClick={() => setShowMore(prev => !prev)}>
                        {showMore ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        {showMore ? 'Hide linked records' : 'View linked records'}
                    </button>
                </div>

                {showMore && (
                    <div className={styles.linksRow}>
                        <a href={`/booking/${item?.bookingIdentifier}`} target="_blank" rel="noopener noreferrer" className={styles.detailLink}>
                            Service Details <ExternalLink size={11} />
                        </a>
                        <a href={`/user/${item?.clientId}`} target="_blank" rel="noopener noreferrer" className={styles.detailLink}>
                            Client Profile <ExternalLink size={11} />
                        </a>
                    </div>
                )}
            </div>
        </div>
    )
}

export default EachFeedbackBox